// React bindings (motiflink §1.3/§1.4).
//
// The public API (`useT`, `useLocale`, `I18nProvider`) is deliberately
// unchanged from the pre-M2 package so the ~38 existing call sites keep
// working: `t('app.badges.title')` and `t('key', { vars })` behave the same.
// What changed underneath is the fallback contract — see ./i18n.ts.
//
// `awaitBundle` exists for the admin app: most `admin.*` strings live only in
// the DB, so mounting before the overlay lands would flash empty chrome. The
// consumer apps render immediately and overlay fire-and-forget.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import { applyDbBundle, getI18n, initialLocale } from './i18n';
import { isLocale, isRtlLocale, localeDirection, type Locale } from './locales';
import { persistLanguage } from './resolve';

export interface TFunction {
  (key: string, vars?: Record<string, string | number>): string;
}

interface I18nContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  /** True until the DB overlay resolves (irrelevant when `awaitBundle`). */
  loading: boolean;
  setLocale: (next: Locale) => void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  /** API origin, e.g. https://api.fajrarabi.com */
  apiBase: string;
  children: ReactNode;
  /**
   * Locale override. Omit to use the module-load resolution chain
   * (query → cookie → localStorage → 'ar'), which is what you usually want.
   */
  locale?: string | null | undefined;
  /** Admin: block the first paint until the DB bundle is merged. */
  awaitBundle?: boolean;
  /** Rendered while `awaitBundle` is pending. */
  fallback?: ReactNode;
}

export function I18nProvider({
  apiBase,
  children,
  locale: forced,
  awaitBundle = false,
  fallback = null,
}: I18nProviderProps): ReactElement {
  const i18n = getI18n();
  const [locale, setLocaleState] = useState<Locale>(() =>
    isLocale(forced) ? forced : initialLocale,
  );
  const [loading, setLoading] = useState(true);

  // Keep i18next + the document in sync whenever the locale changes.
  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
    persistLanguage(locale);
  }, [i18n, locale]);

  // An explicit `locale` prop (e.g. from a /:locale route) wins over the
  // resolution chain, but only when it actually changes.
  useEffect(() => {
    if (isLocale(forced) && forced !== locale) setLocaleState(forced);
  }, [forced, locale]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchBundle(apiBase, locale).then((flat) => {
      if (cancelled) return;
      if (flat) applyDbBundle(locale, flat);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [apiBase, locale]);

  const setLocale = useCallback((next: Locale) => {
    persistLanguage(next);
    setLocaleState(next);
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({ locale, dir: localeDirection(locale), loading, setLocale }),
    [locale, loading, setLocale],
  );

  if (awaitBundle && loading) return <>{fallback}</>;

  return (
    <I18nextProvider i18n={i18n}>
      <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
    </I18nextProvider>
  );
}

/** The one way to render a translated string. */
export function useT(): TFunction {
  const { t } = useTranslation();
  return useCallback<TFunction>((key, vars) => t(key, vars ?? {}), [t]);
}

export function useLocale(): {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  isRtl: boolean;
  setLocale: (next: Locale) => void;
} {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useLocale must be used inside an <I18nProvider>');
  return {
    locale: ctx.locale,
    dir: ctx.dir,
    isRtl: isRtlLocale(ctx.locale),
    setLocale: ctx.setLocale,
  };
}

/**
 * Re-syncs <html dir/lang> on every language change and returns the current
 * direction. The module-load stamp handles the first paint; this handles
 * switches after mount (motiflink §1.4).
 */
export function useLanguageDirection(): 'ltr' | 'rtl' {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useLanguageDirection must be used inside an <I18nProvider>');
  useEffect(() => {
    persistLanguage(ctx.locale);
  }, [ctx.locale]);
  return ctx.dir;
}

// ─── helpers ────────────────────────────────────────────────────────────────

async function fetchBundle(
  apiBase: string,
  locale: Locale,
): Promise<Record<string, string> | null> {
  try {
    const res = await fetch(`${apiBase}/content/translations?lang=${locale}`, {
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { data?: Record<string, string> } | Record<string, string>;
    // Tolerate both the {data} envelope and a bare map.
    return (body as { data?: Record<string, string> }).data ?? (body as Record<string, string>);
  } catch {
    return null; // code bundles still render — that's the point of the split
  }
}
